/**
 * White-label (V1-E14-T03): the brand the dashboard wears. Before sign-in, the address's (`/bff/brand`: a
 * reseller's own domain, or the platform's); signed in, the tenant's (`/v1/branding`: its reseller's, or the
 * platform's). Without one, Swaralo's own. The brand names the product everywhere the UI said Swaralo (the
 * messages), the title and the icon, shows its logo, and sets the primary colour in each theme: the platform has
 * checked each against its background (4.5:1); no colour reads on both, so dark mode without its own keeps
 * Swaralo's.
 */
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { data } from "../api/client";
import { useBff, useMe } from "./session";

export interface Brand {
  productName: string;
  primary: string | null;
  primaryDark: string | null;
  supportEmail: string | null;
  logo: string | null;
  icon: string | null;
}

const LIGHT_BG = "#ffffff";
const DARK_BG = "#0b0f17";
const STYLE_ID = "brand-colours";
const DEFAULT_TITLE = "Swaralo";

function luminance(colour: string): number {
  const channel = (i: number) => {
    const s = parseInt(colour.slice(i, i + 2), 16) / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** WCAG 2's contrast ratio between two #RRGGBB colours. */
export function contrast(a: string, b: string): number {
  const [x, y] = [luminance(a), luminance(b)];
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

const HEX = /^#[0-9a-fA-F]{6}$/;
const colour = (value: unknown): string | null => (typeof value === "string" && HEX.test(value) ? value : null);
const text = (value: unknown): string | null => (typeof value === "string" && value ? value : null);

/** `/bff/brand`'s answer: its image addresses are given. */
export function publicBrand(raw: unknown): Brand | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const productName = text(r.product_name);
  if (!productName) return null;
  return {
    productName, primary: colour(r.primary_color), primaryDark: colour(r.primary_color_dark),
    supportEmail: text(r.support_email), logo: text(r.logo), icon: text(r.icon),
  };
}

/** `/v1/branding`'s answer: its images by their hash, served through the BFF. */
export function tenantBrand(raw: unknown): Brand | null {
  const brand = publicBrand({ ...(raw as object), logo: null, icon: null });
  if (!brand) return null;
  const r = raw as Record<string, unknown>;
  const image = (kind: string) => {
    const sha = text(r[kind]);
    return sha ? `/bff/api/v1/branding/${kind}?v=${sha.slice(0, 16)}` : null;
  };
  return { ...brand, logo: image("logo"), icon: image("icon") };
}

/** The CSS that puts the brand's colours on the design tokens (index.css), per theme. */
export function brandCss(brand: Brand | null): string {
  if (!brand?.primary && !brand?.primaryDark) return "";
  const rules: string[] = [];
  if (brand.primary) rules.push(`:root{--color-primary:${brand.primary};--color-on-primary:${LIGHT_BG}}`);
  // no colour reads 4.5:1 on both backgrounds: dark mode wears the brand's dark colour, or keeps Swaralo's
  if (brand.primaryDark) rules.push(`:root.dark{--color-primary:${brand.primaryDark};--color-on-primary:${DARK_BG}}`);
  return rules.join("\n");
}

function apply(brand: Brand | null): void {
  document.title = brand?.productName ?? DEFAULT_TITLE;
  let style = document.getElementById(STYLE_ID);
  const css = brandCss(brand);
  if (css && !style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  if (style) style.textContent = css;
  let icon = document.querySelector<HTMLLinkElement>("link[rel='icon']");
  if (brand?.icon) {
    if (!icon) {
      icon = document.createElement("link");
      icon.rel = "icon";
      document.head.appendChild(icon);
    }
    icon.href = brand.icon;
  } else if (icon) {
    icon.remove();
  }
}

const BrandContext = createContext<{ brand: Brand | null; setTenant: (brand: Brand | null) => void }>({
  brand: null,
  setTenant: () => undefined,
});

export function useBrand(): Brand | null {
  return useContext(BrandContext).brand;
}

/** Holds the brand; renders nothing until the address's brand is known, so Swaralo never flashes on a reseller's. */
export function BrandProvider({ children }: { children: (brand: Brand | null) => ReactNode }) {
  const bff = useBff();
  const address = useQuery({ queryKey: ["bff", "brand"], queryFn: async () => publicBrand(await bff.brand()), staleTime: Infinity, retry: false });
  const [tenant, setTenant] = useState<Brand | null | undefined>(undefined);
  const brand = tenant !== undefined ? tenant : (address.data ?? null);
  useEffect(() => apply(brand), [brand]);
  if (address.isPending) return null;
  return <BrandContext.Provider value={{ brand, setTenant }}>{children(brand)}</BrandContext.Provider>;
}

/** Signed in: the brand the tenant acted in shows (switching tenant may change it). */
export function TenantBrand() {
  const bff = useBff();
  const me = useMe();
  const { setTenant } = useContext(BrandContext);
  const found = useQuery({
    queryKey: ["brand", me.tenant_id],
    queryFn: async () => tenantBrand(await data(bff.api.GET("/v1/branding"))),
    enabled: me.tenant_id !== null,
    staleTime: 300_000,
  });
  useEffect(() => {
    if (found.isSuccess) setTenant(found.data);
  }, [found.isSuccess, found.data, setTenant]);
  return null;
}
